import React, { useState, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import WeatherDataProcessor from '../../../../packages/data-plugins/weather-fmi/processor/dataProcessor.js';
import { useScreenNavigation } from '../../../../packages/shared/useScreenNavigation';
import { useScreenStateUpdates } from '../../../../packages/shared/useScreenStateUpdates';
import ScreenHeader from '../components/ScreenHeader';
import ScreenNavigation from '../components/ScreenNavigation';

const WeatherContainer = styled.div`
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #74b9ff 0%, #0984e3 100%);
  display: flex;
  flex-direction: column;
  font-family: 'Arial', sans-serif;
  color: white;
`;

const Header = styled.div`
  padding: 20px;
  text-align: center;
  border-bottom: 2px solid rgba(255, 255, 255, 0.2);
`;


const Subtitle = styled.p`
  margin: 10px 0 0 0;
  font-size: 1.2rem;
  opacity: 0.8;
`;

const NavigationBar = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  padding: 20px;
  background: rgba(0, 0, 0, 0.1);
`;

const NavButton = styled.button`
  padding: 10px 20px;
  background: ${props => props.active ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
  border: 2px solid ${props => props.active ? 'white' : 'rgba(255, 255, 255, 0.3)'};
  color: white;
  border-radius: 25px;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: white;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const TimeSelector = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  padding: 20px;
  background: rgba(0, 0, 0, 0.05);
`;

const Select = styled.select`
  padding: 8px 15px;
  border: none;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 1rem;
  cursor: pointer;
  
  option {
    background: #0984e3;
    color: white;
  }
`;

const DataVisualization = styled.div`
  flex: 1;
  padding: 20px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  overflow-y: auto;
`;

const WeatherCard = styled.div`
  background: rgba(255, 255, 255, 0.15);
  border-radius: 15px;
  padding: 20px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const CardTitle = styled.h3`
  margin: 0 0 15px 0;
  font-size: 1.4rem;
  text-align: center;
`;

const MetricValue = styled.div`
  font-size: 2.5rem;
  font-weight: bold;
  text-align: center;
  margin: 10px 0;
`;

const MetricUnit = styled.span`
  font-size: 1rem;
  font-weight: normal;
  opacity: 0.8;
`;

const MetricLabel = styled.div`
  text-align: center;
  opacity: 0.9;
  margin-bottom: 15px;
`;

const TrendChart = styled.div`
  height: 100px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  margin: 15px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-style: italic;
  opacity: 0.7;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 15px;
`;

const StatItem = styled.div`
  text-align: center;
  padding: 10px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  opacity: 0.8;
`;

const StatValue = styled.div`
  font-size: 1.2rem;
  font-weight: bold;
  margin-top: 5px;
`;

const DatasetOverview = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  margin: 10px 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-top: 10px;
`;

const OverviewCard = styled.div`
  text-align: center;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
`;

const OverviewTitle = styled.div`
  font-size: 0.9rem;
  opacity: 0.8;
  margin-bottom: 5px;
`;

const OverviewValue = styled.div`
  font-size: 1.4rem;
  font-weight: bold;
`;

export function WeatherScreen({ screenMode = false }) {
  // Handle screen navigation commands (disabled in screenMode - ScreenClient handles navigation)
  useScreenNavigation({ screenMode });
  
  const [viewMode, setViewMode] = useState('year'); // year, month, day
  const [selectedYear, setSelectedYear] = useState('2024');
  const [selectedMonth, setSelectedMonth] = useState('01');
  const [selectedDay, setSelectedDay] = useState('01');
  const [weatherData, setWeatherData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Calculate weather stats for preview
  const weatherStats = useMemo(() => {
    if (loading) {
      return {
        status: 'Loading weather data...',
        period: `${selectedYear}/${selectedMonth}`,
        viewMode: viewMode
      };
    }
    
    if (error) {
      return {
        status: 'Error loading data',
        period: 'N/A',
        viewMode: viewMode
      };
    }

    return {
      status: 'Weather data active',
      period: `${selectedYear}/${selectedMonth}`,
      viewMode: viewMode.charAt(0).toUpperCase() + viewMode.slice(1) + ' view'
    };
  }, [weatherData, loading, error, selectedYear, selectedMonth, viewMode]);

  // Send screen state updates for facilitator preview
  useScreenStateUpdates('weather', weatherStats);

  const dataProcessor = useMemo(() => new WeatherDataProcessor(), []);

  useEffect(() => {
    loadWeatherData();
  }, []);

  const loadWeatherData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Load real FMI data from cached files
      const allYearsData = {};
      const years = ['2024', '2023', '2022', '2021', '2020', '2019', '2018', '2017', '2016', '2015'];
      
      for (const year of years) {
        try {
          const response = await fetch(`/data/weather-fmi-cache/fmi_historical_helsinki_kaisaniemi_${year}-01-01_${year}-12-31_2025-09-17.json`);
          if (response.ok) {
            const fmiData = await response.json();
            const processedData = dataProcessor.processHistoricalData(fmiData);
            
            // Merge processed data for this year
            Object.assign(allYearsData, {
              yearly: { ...allYearsData.yearly, ...processedData.yearly },
              monthly: { ...allYearsData.monthly, ...processedData.monthly },
              daily: { ...allYearsData.daily, ...processedData.daily }
            });
          } else {
            console.warn(`No data available for year ${year}`);
            // Fall back to mock data for this year
            const mockData = dataProcessor.generateMockData(year);
            Object.assign(allYearsData, {
              yearly: { ...allYearsData.yearly, ...mockData.yearly },
              monthly: { ...allYearsData.monthly, ...mockData.monthly },
              daily: { ...allYearsData.daily, ...mockData.daily }
            });
          }
        } catch (yearError) {
          console.warn(`Failed to load data for ${year}:`, yearError.message);
          // Use mock data as fallback
          const mockData = dataProcessor.generateMockData(year);
          Object.assign(allYearsData, {
            yearly: { ...allYearsData.yearly, ...mockData.yearly },
            monthly: { ...allYearsData.monthly, ...mockData.monthly },
            daily: { ...allYearsData.daily, ...mockData.daily }
          });
        }
      }
      
      setWeatherData(allYearsData);
    } catch (err) {
      setError(err.message);
      console.error('Error loading weather data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentData = () => {
    if (!weatherData) return null;
    
    switch (viewMode) {
      case 'year':
        return weatherData.yearly[selectedYear];
      case 'month':
        return weatherData.monthly[`${selectedYear}-${selectedMonth}`];
      case 'day':
        return weatherData.daily[`${selectedYear}-${selectedMonth}-${selectedDay}`];
      default:
        return null;
    }
  };

  const currentData = getCurrentData();

  const getYears = () => ['2024', '2023', '2022', '2021', '2020', '2019', '2018', '2017', '2016', '2015'];
  const getMonths = () => [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];
  const getDays = () => Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'));

  const formatPeriodTitle = () => {
    switch (viewMode) {
      case 'year':
        return `Year ${selectedYear}`;
      case 'month':
        const month = getMonths().find(m => m.value === selectedMonth);
        return `${month?.label} ${selectedYear}`;
      case 'day':
        const monthName = getMonths().find(m => m.value === selectedMonth);
        return `${monthName?.label} ${selectedDay}, ${selectedYear}`;
      default:
        return '';
    }
  };

  const getDatasetOverview = () => {
    if (!weatherData || !weatherData.yearly) return null;
    
    const years = Object.keys(weatherData.yearly);
    const allYearlyData = Object.values(weatherData.yearly);
    
    const avgTemps = allYearlyData.map(y => y.temperature?.avg || 0).filter(t => t !== 0);
    const totalPrecip = allYearlyData.reduce((sum, y) => sum + (y.precipitation?.total || 0), 0);
    const avgWindSpeed = allYearlyData.map(y => y.wind?.avg || 0).filter(w => w !== 0);
    
    return {
      yearsAvailable: years.length,
      avgTemperature: avgTemps.length ? (avgTemps.reduce((a, b) => a + b, 0) / avgTemps.length).toFixed(1) : 'N/A',
      totalPrecipitation: totalPrecip.toFixed(0),
      avgWindSpeed: avgWindSpeed.length ? (avgWindSpeed.reduce((a, b) => a + b, 0) / avgWindSpeed.length).toFixed(1) : 'N/A',
      dataPoints: Object.keys(weatherData.monthly || {}).length + Object.keys(weatherData.daily || {}).length
    };
  };

  const datasetOverview = getDatasetOverview();

  if (loading) {
    return (
      <WeatherContainer>
        <ScreenHeader 
          title={<ScreenNavigation currentScreenTitle="Weather Context" />}
          subtitle="Loading 10 years (2015-2024) of Finnish weather data from FMI cache"
          rightContent={
            <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem' }}>
              Loading...
            </div>
          }
        />
      </WeatherContainer>
    );
  }

  if (error) {
    return (
      <WeatherContainer>
        <ScreenHeader 
          title={<ScreenNavigation currentScreenTitle="Weather Context" />}
          subtitle={error}
          rightContent={
            <div style={{ color: '#ff6b6b', fontSize: '0.9rem' }}>
              ❌ Error
            </div>
          }
        />
        <ContentArea style={{ justifyContent: 'center', alignItems: 'center' }}>
          <button 
            onClick={loadWeatherData}
            style={{
              padding: '15px 30px',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              border: '2px solid white',
              color: 'white',
              borderRadius: '25px',
              cursor: 'pointer',
              fontSize: '1.1rem'
            }}
          >
            🔄 Retry Loading Data
          </button>
        </ContentArea>
      </WeatherContainer>
    );
  }

  return (
    <WeatherContainer>
      <ScreenHeader 
        title={<ScreenNavigation currentScreenTitle="Weather Context" />}
        subtitle="Helsinki Kaisaniemi Station • 10-Year Dataset (2015-2024) • Finnish Meteorological Institute"
        rightContent={
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.8rem' }}>
              Last updated: {new Date().toLocaleDateString()}
            </div>
            <div style={{ color: '#4CAF50', fontSize: '0.9rem' }}>
              🌤️ Live Data
            </div>
          </div>
        }
      />
      
      <NavigationBar>
        <NavButton 
          active={viewMode === 'year'} 
          onClick={() => setViewMode('year')}
        >
          📅 Yearly View
        </NavButton>
        <NavButton 
          active={viewMode === 'month'} 
          onClick={() => setViewMode('month')}
        >
          📆 Monthly View
        </NavButton>
        <NavButton 
          active={viewMode === 'day'} 
          onClick={() => setViewMode('day')}
        >
          🗓️ Daily View
        </NavButton>
      </NavigationBar>

      <TimeSelector>
        <Select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
          {getYears().map(year => (
            <option key={year} value={year}>Year {year}</option>
          ))}
        </Select>
        
        {(viewMode === 'month' || viewMode === 'day') && (
          <Select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
            {getMonths().map(month => (
              <option key={month.value} value={month.value}>{month.label}</option>
            ))}
          </Select>
        )}
        
        {viewMode === 'day' && (
          <Select value={selectedDay} onChange={(e) => setSelectedDay(e.target.value)}>
            {getDays().map(day => (
              <option key={day} value={day}>Day {day}</option>
            ))}
          </Select>
        )}
      </TimeSelector>

      {datasetOverview && (
        <DatasetOverview>
          <h3 style={{ margin: '0 0 10px 0', fontSize: '1.2rem' }}>📊 Dataset Overview</h3>
          <OverviewGrid>
            <OverviewCard>
              <OverviewTitle>Years Available</OverviewTitle>
              <OverviewValue>{datasetOverview.yearsAvailable}/10</OverviewValue>
            </OverviewCard>
            <OverviewCard>
              <OverviewTitle>Avg Temperature (2015-2024)</OverviewTitle>
              <OverviewValue>{datasetOverview.avgTemperature}°C</OverviewValue>
            </OverviewCard>
            <OverviewCard>
              <OverviewTitle>Total Precipitation (10y)</OverviewTitle>
              <OverviewValue>{datasetOverview.totalPrecipitation}mm</OverviewValue>
            </OverviewCard>
            <OverviewCard>
              <OverviewTitle>Avg Wind Speed (10y)</OverviewTitle>
              <OverviewValue>{datasetOverview.avgWindSpeed}m/s</OverviewValue>
            </OverviewCard>
            <OverviewCard>
              <OverviewTitle>Data Points</OverviewTitle>
              <OverviewValue>{datasetOverview.dataPoints.toLocaleString()}</OverviewValue>
            </OverviewCard>
          </OverviewGrid>
        </DatasetOverview>
      )}

      <ContentArea>
        <Header style={{ borderBottom: 'none', padding: '10px 20px' }}>
          <h2 style={{ margin: 0, fontSize: '1.8rem' }}>{formatPeriodTitle()}</h2>
        </Header>

        {currentData ? (
          <DataVisualization>
            {/* Temperature Card */}
            <WeatherCard>
              <CardTitle>🌡️ Temperature</CardTitle>
              <MetricValue>
                {currentData.temperature.avg.toFixed(1)}
                <MetricUnit>°C</MetricUnit>
              </MetricValue>
              <MetricLabel>Average Temperature</MetricLabel>
              <StatsGrid>
                <StatItem>
                  <StatLabel>Minimum</StatLabel>
                  <StatValue>{currentData.temperature.min.toFixed(1)}°C</StatValue>
                </StatItem>
                <StatItem>
                  <StatLabel>Maximum</StatLabel>
                  <StatValue>{currentData.temperature.max.toFixed(1)}°C</StatValue>
                </StatItem>
              </StatsGrid>
              <TrendChart>Temperature trend visualization</TrendChart>
            </WeatherCard>

            {/* Humidity Card */}
            <WeatherCard>
              <CardTitle>💧 Humidity</CardTitle>
              <MetricValue>
                {parseFloat(currentData.humidity.avg).toFixed(2)}
                <MetricUnit>%</MetricUnit>
              </MetricValue>
              <MetricLabel>Average Relative Humidity</MetricLabel>
              <StatsGrid>
                <StatItem>
                  <StatLabel>Minimum</StatLabel>
                  <StatValue>{parseFloat(currentData.humidity.min).toFixed(2)}%</StatValue>
                </StatItem>
                <StatItem>
                  <StatLabel>Maximum</StatLabel>
                  <StatValue>{parseFloat(currentData.humidity.max).toFixed(2)}%</StatValue>
                </StatItem>
              </StatsGrid>
              <TrendChart>Humidity trend visualization</TrendChart>
            </WeatherCard>

            {/* Precipitation Card */}
            <WeatherCard>
              <CardTitle>🌧️ Precipitation</CardTitle>
              <MetricValue>
                {parseFloat(currentData.precipitation.total).toFixed(2)}
                <MetricUnit>mm</MetricUnit>
              </MetricValue>
              <MetricLabel>
                Total Precipitation ({viewMode === 'day' ? 'hours' : 'days'} with rain: {
                  currentData.precipitation.days || currentData.precipitation.hours || 0
                })
              </MetricLabel>
              <TrendChart>Precipitation pattern visualization</TrendChart>
            </WeatherCard>

            {/* Wind Card */}
            <WeatherCard>
              <CardTitle>💨 Wind</CardTitle>
              <MetricValue>
                {currentData.wind.avg.toFixed(1)}
                <MetricUnit>m/s</MetricUnit>
              </MetricValue>
              <MetricLabel>Average Wind Speed</MetricLabel>
              <StatsGrid>
                <StatItem>
                  <StatLabel>Maximum Gust</StatLabel>
                  <StatValue>{currentData.wind.max.toFixed(1)} m/s</StatValue>
                </StatItem>
                <StatItem>
                  <StatLabel>Beaufort Scale</StatLabel>
                  <StatValue>
                    {currentData.wind.avg < 1 ? 'Calm' :
                     currentData.wind.avg < 3 ? 'Light' :
                     currentData.wind.avg < 7 ? 'Moderate' :
                     currentData.wind.avg < 14 ? 'Strong' : 'Gale'}
                  </StatValue>
                </StatItem>
              </StatsGrid>
              <TrendChart>Wind speed and direction visualization</TrendChart>
            </WeatherCard>
          </DataVisualization>
        ) : (
          <DataVisualization>
            <WeatherCard>
              <CardTitle>No Data Available</CardTitle>
              <MetricLabel>
                No weather data available for {formatPeriodTitle().toLowerCase()}
              </MetricLabel>
            </WeatherCard>
          </DataVisualization>
        )}
      </ContentArea>
    </WeatherContainer>
  );
}

export default WeatherScreen;